import React  from "react";
import { useState, useContext } from "react";
import { Card, Button, Col, Form, Row, Collapse} from "react-bootstrap";
import { MedicalLicenseList } from "../../../Shared/Components/MedicalLicenseList";
import { deepCopy } from "../../../Shared/Utility/DeepCopy";
import { StateWithCountryLookupItem } from "../../../Shared/Models/StateWithCountryLookupItem";
import { abpnClient } from "../../../../Services/AbpnClient";
import { MedicalLicense } from "../../../Physician/Models/MedicalLicense";
import { AuthContext } from "../../../Auth/AuthProvider";
import AbpnSpinner from "../../../Shared/Components/AbpnSpinner";
import { getDateWithOffset } from "../../../Shared/Utility/GetDateWithOffset";

interface IProps {
    medicalLicenses: Array<MedicalLicense>;
    states: Array<StateWithCountryLookupItem>;
    onSaveLicenses: (medicalLicenses: Array<MedicalLicense>) => void;
    isSectionOpen: boolean;
    showCollapse: boolean;
}

export function MedicalLicensure(props: IProps) {
    const [medicalLicenses, setMedicalLicenses] = useState<Array<MedicalLicense>>(props.medicalLicenses);
    const [openCollapse, setOpenCollapse] = useState<boolean>(props.isSectionOpen);
    const authContext = useContext(AuthContext);

    const validate = (medicalLicenses: Array<MedicalLicense>) : boolean => {
        medicalLicenses.forEach((medicalLicense: MedicalLicense) => {
            medicalLicense.validationMessage = new Array<string>();
            
            if (medicalLicense.medicalLicenseId === 0 && getDateWithOffset(medicalLicense.expirationDate) < new Date())
            {
                medicalLicense.validationMessage.push("Can not add expired licenses.");
            }
            if (!medicalLicense.licenseNumber?.trim()) {
                medicalLicense.validationMessage.push(" License number is required.");
            }
            if (!medicalLicense.stateId) {
                medicalLicense.validationMessage.push(" State is required");
            }
            if (!Date.parse(medicalLicense.expirationDate)){
                medicalLicense.validationMessage.push(" Expiration date invalid. Expected format: MM/DD/YYYY");
            }
            if (medicalLicense.validationMessage.length > 0){
                medicalLicense.isEditing = true;
            }
        })
        setMedicalLicenses(deepCopy(medicalLicenses));
        return !medicalLicenses.some(_ => _.validationMessage?.length > 0);
    }

    const saveAllMedicalLicenses = (medicalLicensesToUpdate: Array<MedicalLicense>) => {
        if (validate(medicalLicensesToUpdate))
        {
            const url = "/api/Physician/SaveMedicalLicenses/";
            abpnClient.post<Array<MedicalLicense>>(url, medicalLicensesToUpdate, authContext).then(result => {
                result.forEach((license: MedicalLicense) => { license.isEditing = false; license.isNew = false });
                setMedicalLicenses(result);
                props.onSaveLicenses(result);
            });
        }   
    }

    const addNewMedicalLicense = () => {
        var newMedicalLicense = new MedicalLicense();
        newMedicalLicense.isNew = true;
        newMedicalLicense.isEditing = true;
        var newList = deepCopy(medicalLicenses);
        newList.push(newMedicalLicense);
        setMedicalLicenses(newList);
    }

    const removeMedicalLicense = (medicalLicense: MedicalLicense) => {
        const url = "/api/Physician/DeleteMedicalLicense/";
        abpnClient.post<void>(url, medicalLicense, authContext).then(() => {
            var newList = deepCopy(medicalLicenses.filter(x => x.medicalLicenseId !== medicalLicense.medicalLicenseId));
            setMedicalLicenses(newList);
        });
    }

    const updateMedicalLicense = (medicalLicense: MedicalLicense) => {
        var updated = deepCopy(medicalLicenses)
        var index = updated.findIndex(_ => _.medicalLicenseId == medicalLicense.medicalLicenseId);
        updated[index] = medicalLicense;
        saveAllMedicalLicenses(updated);
    }

    const canAddNewLicense = () => {
        return medicalLicenses.every(_ => _.isNew !== true);
    }

    const areLicensesValid = () => {
        return !medicalLicenses.some(_ => _.validationMessage?.length > 0);
    }

    const toggleCollaspe = () => {
        if(props.showCollapse){
            setOpenCollapse(!openCollapse)
        }
    }

    if (props.medicalLicenses == null) return <AbpnSpinner></AbpnSpinner>;
    return (
        <Card className="mb-4" border="pp-secondary-grey">
            <Card.Body>
                <Card.Title className="mb-0 font-weight-bolder" onClick={toggleCollaspe}>
                    <span className="text-pp-primary">{props.showCollapse && <span><i className="font-weight-bolder las la-angle-down"></i> </span> }Medical Licensure</span>	
                </Card.Title>
                <Collapse in={openCollapse}>
                    <Row className="mt-3">
                        <Col xs={12} md={12} lg={12} xl={12}>
                            <Form noValidate>
                                <MedicalLicenseList medicalLicenses={medicalLicenses} states={props.states}
                                    handleRemove={(medicalLicense: MedicalLicense) => removeMedicalLicense(medicalLicense)}
                                    updateMedicalLicense={(medicalLicense: MedicalLicense) => updateMedicalLicense(medicalLicense)} />

                                <Card bsPrefix="border-0 pp-align-self-end mt-2">
                                    <Button variant="outline-pp-primary" disabled={!areLicensesValid() || !canAddNewLicense()} onClick={() => addNewMedicalLicense()}><strong>Add License</strong></Button>
                                </Card>
                            </Form>
                        </Col>
                    </Row>
                </Collapse>
            </Card.Body>
        </Card>
    );
}

export default MedicalLicensure;